// utils/downloadBase64File.helper.ts

type DownloadBase64FileParams = {
  base64Data: string;
  fileName?: string;
  fallbackName?: string;
};

// 🔹 Detect MIME type from base64 header
const getMimeTypeFromBase64 = (base64: string): string => {
  if (base64.startsWith('JVBERi0')) return 'application/pdf';
  if (base64.startsWith('iVBORw0KGgo')) return 'image/png';
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  if (base64.startsWith('R0lGOD')) return 'image/gif';
  if (base64.startsWith('UEsDB')) return 'application/zip'; // docx/xlsx
  return 'application/octet-stream';
};

// 🔹 Get file extension
const getExtensionFromMime = (mime: string): string => {
  switch (mime) {
    case 'application/pdf':
      return 'pdf';
    case 'image/png':
      return 'png';
    case 'image/jpeg':
      return 'jpg';
    case 'image/gif':
      return 'gif';
    case 'application/zip':
      return 'zip';
    default:
      return 'bin';
  }
};

// 🔹 Main helper
export const downloadBase64File = ({
  base64Data,
  fileName,
  fallbackName = 'file',
}: DownloadBase64FileParams) => {
  if (!base64Data || typeof base64Data !== 'string') {
    console.error('Invalid base64 data');
    return;
  }

  const mimeType = getMimeTypeFromBase64(base64Data);
  const extension = getExtensionFromMime(mimeType);

  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));

  const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName
    ? `${fileName}.${extension}`
    : `${fallbackName}.${extension}`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
